/**
 * DataService - Handles loading and processing race data
 */

import * as d3 from 'd3';
import { AthleteData } from '../types/athlete.types';
import { CSVRow, StageNumber } from '../types/stage.types';
import { TOTAL_STAGES } from '../constants';
import { getRaceConfig } from '../utils/urlParams';

export class DataService {
    private data: Map<StageNumber, AthleteData[]> = new Map();

    /**
     * Loads race data from CSV file
     */
    async loadData(): Promise<void> {
        try {
            const raceConfig = getRaceConfig();
            console.log('Loading race data:', raceConfig.name, 'from', raceConfig.csvFile);

            const csvData = await d3.csv(raceConfig.csvFile);

            // Process data for each stage
            for (let stage = 1; stage <= TOTAL_STAGES; stage++) {
                const stageData = this.processStageData(csvData as unknown as CSVRow[], stage as StageNumber);
                this.data.set(stage as StageNumber, stageData);
            }

            console.log('Data loaded successfully. Total stages:', this.data.size);
        } catch (error) {
            console.error('Error loading data:', error);
            throw error;
        }
    }

    /**
     * Processes raw CSV data for a specific stage
     */
    private processStageData(csvData: CSVRow[], stage: StageNumber): AthleteData[] {
        const stageData = csvData.map(row => {
            const athlete: AthleteData = {
                Rank: +row.Rank,
                Bib: +row.Bib,
                Name: row.Name,
                Distance: row.Distance,
                'Age Group': row['Age Group'],
                Event: row.Event,
                Club: row.Club,
                Country: row.Country,
                stageTime: 0,
                stageRank: 0,
                isIncomplete: false
            };

            // Calculate cumulative time up to this stage
            let cumulativeTime = 0;
            let hasValidTimes = true;

            for (let i = 1; i <= stage; i++) {
                const runTimeStr = row[`Run${i}` as const];
                const runTime = +row[`Run${i}_seconds` as const] || 0;

                if (!runTimeStr || runTimeStr.includes('DNS') || runTimeStr.includes('DNF') || runTime === 0) {
                    hasValidTimes = false;
                    break;
                }
                cumulativeTime += runTime;
            }

            if (!hasValidTimes) {
                athlete.stageTime = Number.MAX_SAFE_INTEGER;
                athlete.isIncomplete = true;
            } else {
                athlete.stageTime = cumulativeTime;
            }

            // Store individual run times
            for (let i = 1; i <= TOTAL_STAGES; i++) {
                athlete[`Run${i}_seconds` as const] = +row[`Run${i}_seconds` as const] || 0;
            }

            return athlete;
        }).filter(athlete => athlete.stageTime < Number.MAX_SAFE_INTEGER);

        // Sort by cumulative time
        stageData.sort((a, b) => a.stageTime - b.stageTime);

        // Assign stage-specific rankings
        stageData.forEach((athlete, index) => {
            athlete.stageRank = index + 1;
        });

        // Calculate individual race rankings
        if (stage > 0) {
            const individualRanking = [...stageData].sort((a, b) => {
                const aRaceTime = a[`Run${stage}_seconds` as const] || Number.MAX_SAFE_INTEGER;
                const bRaceTime = b[`Run${stage}_seconds` as const] || Number.MAX_SAFE_INTEGER;
                return aRaceTime - bRaceTime;
            });

            individualRanking.forEach((athlete, index) => {
                const originalAthlete = stageData.find(a => a.Bib === athlete.Bib);
                if (originalAthlete) {
                    originalAthlete.individualRaceRank = index + 1;
                    originalAthlete.individualRaceTime = athlete[`Run${stage}_seconds` as const];
                }
            });
        }

        return stageData;
    }

    /**
     * Gets data for a specific stage
     */
    getStageData(stage: StageNumber): AthleteData[] {
        return this.data.get(stage) || [];
    }

    /**
     * Gets all data (useful for search across all stages)
     */
    getAllData(): Map<StageNumber, AthleteData[]> {
        return this.data;
    }

    /**
     * Checks if data is loaded
     */
    isLoaded(): boolean {
        return this.data.size > 0;
    }
}
